import React, { useMemo } from 'react';
import {
  groupPopularidadInDisplayOrder,
  mergePopularidadEncabezado,
  normalizePopularidadRow,
  POPULARIDAD_COLUMN_LABELS,
  sumGroupTotals,
} from '../../../utils/popularidadReportModel';
import { encabezadoTieneDatos } from '../../../utils/reporteEncabezadoApi';
import { isoADisplayDDMMYYYYGuiones } from '../../../utils/fechaReporte';
import ReporteFiltrosEncabezadoLista from '../ReporteFiltrosEncabezadoLista/ReporteFiltrosEncabezadoLista';
import './PopularidadReportLayout.css';

const money = (n) =>
  n == null || Number.isNaN(n)
    ? '—'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const numCell = (n) =>
  n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString('es-MX', { maximumFractionDigits: 2 });

const formatFechaHeader = (iso) =>
  iso ? isoADisplayDDMMYYYYGuiones(String(iso).split('T')[0]) : '—';

const meaningfulHeaderLine = (v) => v != null && String(v).trim() !== '';

export default function PopularidadReportLayout({
  className = '',
  rawRows = [],
  fechaInicio,
  fechaFin,
  cajaInicio,
  cajaFin,
  turnoInicio,
  turnoFin,
  encabezadoApi = null,
  encabezadoFallback = null,
  reporteTitulo = 'REPORTE POPULARIDAD DE PRODUCTOS',
  incluirCortesias = false,
  footer = {},
  sucursalNombre = '',
  bebidasAlcoholicas = false,
  incluirPrecioMenu = true,
}) {
  const enc = useMemo(
    () =>
      mergePopularidadEncabezado(encabezadoApi, encabezadoFallback, {
        reporteTitulo,
        incluirCortesias,
        bebidasAlcoholicas,
      }),
    [encabezadoApi, encabezadoFallback, reporteTitulo, incluirCortesias, bebidasAlcoholicas]
  );

  const { groups, grand } = useMemo(() => {
    const normalized = rawRows.map(normalizePopularidadRow).filter(Boolean);
    const g = groupPopularidadInDisplayOrder(normalized);
    const gr = sumGroupTotals(normalized);
    return { groups: g, grand: gr };
  }, [rawRows]);

  /* Anchos fijos compartidos por todas las tablas para que columnas alineen */
  const colWidths = incluirPrecioMenu
    ? ['9%', null, '8%', '9%', '9%', '9%', '10%', '10%']
    : ['9%', null, '8%', '9%', '9%', '10%', '10%'];

  const TableColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={w ? { width: w } : {}} />
      ))}
    </colgroup>
  );

  const responsable = String(
    enc.responsable ||
      enc.nombreResponsable ||
      enc.representante ||
      enc.representanteLegal ||
      enc.nombreCompletoUsuario ||
      enc.nombreUsuario ||
      ''
  ).trim();

  if (!rawRows.length) {
    return (
      <div className={`pop-report pop-report--empty ${className}`.trim()}>
        Sin resultados para el rango seleccionado.
      </div>
    );
  }

  return (
    <div className={`pop-report ${className}`.trim()}>
      <header className="pop-report-headerenc">
        {encabezadoTieneDatos(enc) &&
        enc.bloqueTexto &&
        !enc.nombreComercial &&
        !enc.reporteTitulo ? (
          <pre className="pop-report-headerenc__bloque">{enc.bloqueTexto}</pre>
        ) : (
          <>
            {(meaningfulHeaderLine(enc.nombreComercial) ||
              meaningfulHeaderLine(responsable) ||
              meaningfulHeaderLine(enc.domicilioFiscal) ||
              meaningfulHeaderLine(enc.rfc)) && (
              <div className="pop-report-headerenc__empresa">
                {meaningfulHeaderLine(enc.nombreComercial) && (
                  <h1 className="pop-report-headerenc__nombre">
                    {String(enc.nombreComercial).trim().toUpperCase()}
                    {sucursalNombre && !String(enc.nombreComercial).toUpperCase().includes(sucursalNombre.toUpperCase()) && (
                      <span className="pop-report-headerenc__sucursal-append"> - {sucursalNombre.toUpperCase()}</span>
                    )}
                  </h1>
                )}
                {meaningfulHeaderLine(responsable) && (
                  <p className="pop-report-headerenc__detalle">
                    {String(responsable).toUpperCase()}
                  </p>
                )}
                {meaningfulHeaderLine(enc.domicilioFiscal) && (
                  <p className="pop-report-headerenc__detalle">
                    {String(enc.domicilioFiscal).trim().toUpperCase()}
                  </p>
                )}
                {meaningfulHeaderLine(enc.rfc) && (
                  <p className="pop-report-headerenc__detalle pop-report-headerenc__rfc">
                    RFC: {String(enc.rfc).trim().toUpperCase()}
                  </p>
                )}
              </div>
            )}
            <div className="pop-report-headerenc__titulos">
              <h2 className="pop-report-headerenc__reporte">
                {String(enc.reporteTitulo || reporteTitulo).toUpperCase()}
              </h2>
              {meaningfulHeaderLine(enc.cortesias) && (
                <p className="pop-report-headerenc__cortesias">
                  {String(enc.cortesias).toUpperCase()}
                </p>
              )}
            </div>
            <div className="pop-report-headerenc__filtros">
              <p className="pop-report-headerenc__filtros-line">
                {enc.periodoTexto ? (
                  enc.periodoTexto
                ) : (
                  <>
                    Del: <strong>{formatFechaHeader(fechaInicio)}</strong> al{' '}
                    <strong>{formatFechaHeader(fechaFin)}</strong>
                  </>
                )}
              </p>
              {enc.filtrosTexto && (
                <p className="pop-report-headerenc__filtros-line pop-report-headerenc__filtros-line--extra">
                  {enc.filtrosTexto}
                </p>
              )}
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report"
                label="Sucursales"
                texto={enc.sucursalesTexto || sucursalNombre}
                items={enc.sucursalesItems}
                uppercase
              />
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report"
                label="Cajas"
                texto={
                  enc.cajasTexto ||
                  (cajaInicio != null || cajaFin != null
                    ? `${cajaInicio ?? '—'} a ${cajaFin ?? '—'}`
                    : '')
                }
                items={enc.cajasItems}
                uppercase
              />
              <p className="pop-report-headerenc__filtros-line">
                {enc.turnosTexto ? (
                  <>Turnos: {String(enc.turnosTexto).toUpperCase()}</>
                ) : (
                  <>
                    Turnos: <strong>{turnoInicio ?? '—'}</strong> a <strong>{turnoFin ?? '—'}</strong>
                  </>
                )}
              </p>
            </div>
          </>
        )}
      </header>

      {groups.map(([familia, items]) => {
        const sub = sumGroupTotals(items);
        return (
          <section key={familia} className="pop-report-block">
            <h3 className="pop-report-familia">{familia}</h3>
            <div className="pop-report-table-wrap">
              <table className="pop-report-table" style={{ tableLayout: 'fixed' }}>
                <TableColGroup />
                <thead>
                  <tr>
                    {POPULARIDAD_COLUMN_LABELS.filter((h) => incluirPrecioMenu || h !== 'Precio').map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((r, idx) => (
                    <tr key={`${familia}-${r.codigo}-${idx}`}>
                      <td>{r.codigo}</td>
                      <td>{r.nombre}</td>
                      <td className="pop-num">{numCell(r.cantidad)}</td>
                      <td className="pop-num">{money(r.costo)}</td>
                      <td className="pop-num">{money(r.tCosto)}</td>
                      {incluirPrecioMenu && <td className="pop-num">{money(r.precio)}</td>}
                      <td className="pop-num">{money(r.importe)}</td>
                      <td className="pop-num">{money(r.utilidad)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="pop-report-subtotal">
                    <td colSpan={2} className="pop-report-subtotal-label">
                      Total {familia}
                    </td>
                    <td className="pop-num">{numCell(sub.cantidad)}</td>
                    <td className="pop-num">—</td>
                    <td className="pop-num">{money(sub.tCosto)}</td>
                    {incluirPrecioMenu && <td className="pop-num">—</td>}
                    <td className="pop-num">{money(sub.importe)}</td>
                    <td className="pop-num">{money(sub.utilidad)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        );
      })}

      <div className="pop-report-global-wrap">
        <table className="pop-report-table pop-report-global" style={{ tableLayout: 'fixed' }}>
          <TableColGroup />
          <tbody>
            <tr className="pop-report-global-row">
              <td colSpan={2} className="pop-report-subtotal-label">
                Total Global
              </td>
              <td className="pop-num">{numCell(grand.cantidad)}</td>
              <td className="pop-num">—</td>
              <td className="pop-num">{money(grand.tCosto)}</td>
              {incluirPrecioMenu && <td className="pop-num">—</td>}
              <td className="pop-num">{money(grand.importe)}</td>
              <td className="pop-num">{money(grand.utilidad)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {bebidasAlcoholicas &&
        ((grand.impuestoBA != null && grand.impuestoBA !== 0) ||
          (footer?.subtotal != null && !Number.isNaN(Number(footer.subtotal)))) && (
        <footer className="pop-report-footer-extras">
          {footer?.subtotal != null && !Number.isNaN(Number(footer.subtotal)) && (
            <p className="pop-report-footer-line">
              <span>Subtotal</span>
              <strong>{money(footer.subtotal)}</strong>
            </p>
          )}
          {bebidasAlcoholicas && grand.impuestoBA != null && grand.impuestoBA !== 0 && (
            <p className="pop-report-footer-line">
              <span>Impuesto bebidas alcohólicas</span>
              <strong>{money(grand.impuestoBA)}</strong>
            </p>
          )}
        </footer>
      )}

    </div>
  );
}
